import React, { useEffect, useState } from "react"

import {
  Backdrop,
  Box,
  darken,
  lighten,
  Paper,
  Popper,
  styled,
  Typography,
  useTheme
} from "@mui/material"

import { useLocation } from "react-router-dom"

import HKVLink from "../HKVLink"
import { ChevronRightRounded } from "@mui/icons-material"

const StyledDesktopMenu = styled("nav", {
})(({ theme }) => {
  return {
    display: "flex",
    height: "100%",
    [theme.breakpoints.down("md")]: {
      display: "none"
    }
  }
})

const StyledMenuItem = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "active" && prop !== "subpage" && prop !== "open",
})(({ theme, active, open, subpage }) => {
  return (
    {
      display: "flex",
      alignItems: "center",
      height: "100%",
      fontSize: theme.typography.pxToRem(18),
      color: theme.palette.text.primary,
      cursor: "pointer",
      padding: theme.spacing(1, 2),
      boxSizing: "border-box",
      boxShadow: "inset 0px -3px 0px black transparent",
      "&:hover": {
        color: theme.palette.text.primary,
        background: subpage ? "inherit" : darken(theme.palette.geel.main, 0.1),
      },
      "&:focus": {
        color: theme.palette.text.primary
      },
      "&:visited": {
        color: theme.palette.text.primary
      },
      ...open && {
        background: darken(theme.palette.geel.main, 0.1)
      },
      ...active && subpage && {
        textDecoration: "underline"
      },
      ...active && !subpage && {
        background: lighten(theme.palette.geel.main, 0.65),
        boxShadow: `inset 0px -3px 0px ${theme.palette.hemelblauw.main}`
      }
    }
  )
})

const DesktopMenu = (props) => {
  const { pages } = props

  const theme = useTheme()

  const [anchorEl, setAnchorEl] = useState(null)
  const [openSubpages, setOpenSubpages] = useState({})

  const location = useLocation()
  const pathname = location.pathname

  const findSubpagePath = (pages = [], pathname) => {
    const result = []

    const search = (subs = [], level = 0) => {
      for (const page of subs) {
        if (pathname === page.path || pathname.startsWith(`${page.path}/`)) {
          result[level] = subs
          if (page.subpages) {
            search(page.subpages, level + 1)
          }
          break
        }
      }
    }

    search(pages)
    return result.length > 0 ? result : null
  }

  const handleMouseEnter = (event, subpages) => {
    if (subpages) {
      setAnchorEl(event.currentTarget)

      const matchedSubpages = findSubpagePath(subpages, pathname)

      if (matchedSubpages) {
        setOpenSubpages(
          matchedSubpages.reduce((acc, val, index) => {
            acc[index] = val
            return acc
          }, {})
        )
      } else {
        setOpenSubpages(prevState => {
          prevState[0] = subpages
          return { ...prevState }
        })
      }
    }
  }

  const handleMouseLeave = () => {
    setAnchorEl(null)
    setOpenSubpages({})
  }

  return (
    <StyledDesktopMenu>
      <Backdrop
        open={anchorEl}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          top: anchorEl ? anchorEl.getBoundingClientRect().bottom : 0,
        }}
        transitionDuration={{ exit: 0 }}
      />
      {pages.map((page, ix) => {
        const isPageActive = pathname === page.path || pathname.startsWith(`${page.path}/`)
        const hasSubpages = page?.subpages?.length > 0

        return (
          <div
            key={ix}
            onMouseEnter={(e) => handleMouseEnter(e, page.subpages)}
            onMouseLeave={handleMouseLeave}
          >
            <HKVLink
              to={page.path}
              sx={{ height: "100%" }}
            >
              <StyledMenuItem
                active={isPageActive}
                open={hasSubpages && openSubpages[0] === page.subpages}
                component="span"
              >
                {page.label}
              </StyledMenuItem>
            </HKVLink>

            {hasSubpages && (
              <Popper
                open={anchorEl && openSubpages[0] === page.subpages}
                anchorEl={anchorEl}
                placement="bottom-start"
                disablePortal
                className="submenu"
                sx={{
                  left: "0px !important",
                  right: "0px !important",
                  zIndex: 1300
                }}
              >
                <Paper
                  square
                  elevation={4}
                  className="sub-menu"
                >
                  <Box
                    sx={{
                      display: "grid",
                      maxWidth: 1200,
                      padding: theme.spacing(5, 0, 6),
                      margin: "auto"
                    }}
                  >
                    <HKVLink
                      to={page.path}
                      onClick={handleMouseLeave}
                      sx={{ width: "fit-content" }}
                    >
                      <Typography
                        variant="h2"
                        color="lintblauw"
                        sx={{
                          paddingBottom: theme.spacing(1),
                          marginBottom: theme.spacing(1),
                        }}
                        onMouseEnter={() => {
                          setOpenSubpages(prevState => {
                            prevState = prevState[0] ? { 0: prevState[0] } : {}
                            return { ...prevState }
                          })
                        }}
                      >
                        {page.label}
                      </Typography>
                    </HKVLink>

                    <div
                      style={{
                        display: "flex",
                        borderTop: `1px solid ${theme.palette.grey[200]}`,
                        paddingTop: theme.spacing(1)
                      }}
                    >
                      {Object.values(openSubpages).map((openSubPage, i) => (
                        <div key={i} style={{ display: "grid", height: "fit-content" }}>
                          {openSubPage.map((subpage, subIx) => {
                            const isSubActive = pathname === subpage.path || pathname.startsWith(`${subpage.path}/`)

                            return (
                              <HKVLink
                                key={subIx}
                                to={subpage.path}
                                onClick={handleMouseLeave}
                                sx={{ width: "fit-content" }}
                                onMouseEnter={() => setOpenSubpages(prevState => {
                                  prevState[i + 1] = subpage.subpages
                                  return { ...prevState }
                                })}
                              >
                                <StyledMenuItem active={isSubActive} component="span" subpage>
                                  {subpage.label}
                                  <ChevronRightRounded
                                    sx={{
                                      margin: theme.spacing(0, 1, 0, 2),
                                      opacity: subpage.subpages?.length > 0 && subpage.subpages === openSubpages[i + 1] ? 1 : 0
                                    }}
                                  />
                                </StyledMenuItem>
                              </HKVLink>
                            )
                          })}
                        </div>
                      ))}
                    </div>
                  </Box>
                </Paper>
              </Popper>
            )}
          </div>
        )
      })}
    </StyledDesktopMenu>
  )
}

export default React.memo(DesktopMenu)